import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { GhostButton } from "@/shared/components/ui-kit";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { saveBlob } from "@/shared/components/viewer/files";
import { useMediaQuery } from "@/shared/hooks/useViewport";
import { useSettings } from "@/shared/api/settings";
import {
  getKbNodeFile, useCreateKbFolder, useKbNodes, useKnowledgeBases, usePatchKbNode, useReindexKbNode,
} from "@/shared/api/knowledge";
import { KbAddMenu, type AddKind } from "./KbAddMenu";
import { KbBreadcrumb } from "./KbBreadcrumb";
import { DeleteKbDialog, KbFormDialog } from "./KbDialogs";
import { KbFileView } from "./KbFileView";
import { KbFolderView } from "./KbFolderView";
import { DeleteNodeDialog, MoveNodeDialog, NewFolderDialog } from "./KbNodeDialogs";
import { KbNoteDialog } from "./KbNoteDialog";
import { KbSidebar } from "./KbSidebar";
import { toUploads } from "./uploads";
import { useKbUpload } from "./hooks/useKbUpload";
import { useListing } from "./hooks/useListing";
import { useTree } from "./hooks/useTree";
import type { NodeEdits } from "./KbRow";

import type { KbNode, NavigationOptions } from "@/shared/types";

type Dialog =
  | { kind: "folder" } | { kind: "note" } | { kind: "edit-base" } | { kind: "delete-base" }
  | { kind: "move"; node: KbNode } | { kind: "delete"; node: KbNode };

/* One knowledge base as a drive: the rail of every base on the left, and on the right the
   open folder as a table, or the open file. On a phone the rail steps aside: the breadcrumb
   walks back and New sits in the toolbar. What is open rides the address
   (`/knowledge/<kb>/<node>`), so the page reads it and asks `onOpen` to change it. */
export default function KnowledgeBasePage({ kbId, nodeId = null, onOpen }: {
  kbId: string;
  nodeId?: string | null;
  onOpen: (kbId: string | null, nodeId?: string | null, how?: NavigationOptions) => void;
}) {
  const { data: bases } = useKnowledgeBases();
  const { data: nodes, isPending, error, refetch } = useKbNodes(kbId);
  const { data: settings } = useSettings();
  const tree = useTree(nodes);
  const base = bases?.find((b) => b.id === kbId);
  const wide = useMediaQuery("(min-width: 768px)");
  const listing = useListing(kbId);
  const upload = useKbUpload();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const createFolder = useCreateKbFolder();
  const patch = usePatchKbNode();
  const reindex = useReindexKbNode();

  const open = tree.byId(nodeId);
  const folderId = open?.kind === "file" ? open.parent_id : open ? open.id : null; // where New puts things
  const path = tree.pathTo(folderId);
  const here = path[path.length - 1]?.name ?? base?.name ?? "this base";
  const goTo = (id: string | null) => {
    listing.setQuery(""); // a folder opened is left, not searched
    onOpen(kbId, id);
  };
  const openNode = (node: KbNode) => (node.kind === "folder" ? goTo(node.id) : onOpen(kbId, node.id));

  // A node deleted or moved out from under the page leaves it at its last folder still there.
  const last = useRef<KbNode | undefined>(undefined);
  if (open) last.current = open;
  useEffect(() => {
    if (!nodeId || !nodes || open) return;
    const parent = last.current?.parent_id;
    onOpen(kbId, parent && nodes.some((n) => n.id === parent) ? parent : null, { replace: true });
  });

  const fail = (e: Error) => toast.error(e.message);
  const edits: NodeEdits = {
    rename: (n, name) => patch.mutate({ kbId, nodeId: n.id, body: { name } }, { onError: fail }),
    move: (n) => setDialog({ kind: "move", node: n }),
    remove: (n) => setDialog({ kind: "delete", node: n }),
    reindex: (n) => reindex.mutate({ kbId, nodeId: n.id }, { onError: fail }),
    download: (n) => void getKbNodeFile(kbId, n.id).then((blob) => saveBlob(blob, n.name), fail),
  };
  const add = (kind: AddKind) => {
    if (kind === "files") filesInput.current?.click();
    else if (kind === "folder-upload") folderInput.current?.click();
    else setDialog({ kind: kind === "note" ? "note" : "folder" });
  };
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    upload.start(toUploads(files), kbId, folderId);
  };
  const close = () => setDialog(null);

  return (
    <main className="flex-1 min-w-0 flex bg-[var(--canvas)]">
      {/* `webkitdirectory` is not in React's input typings. */}
      <input ref={filesInput} type="file" multiple className="hidden" aria-label="Upload files" onChange={pick} />
      <input
        ref={folderInput}
        type="file"
        multiple
        className="hidden"
        aria-label="Upload folder"
        onChange={pick}
        {...({ webkitdirectory: "" } as React.InputHTMLAttributes<HTMLInputElement>)}
      />

      {wide && (
        <KbSidebar
          kbId={kbId}
          folderPath={path}
          place={here}
          onOpen={(id, folder) => { listing.setQuery(""); onOpen(id, folder); }}
          onAdd={add}
          onDrop={upload.start}
          busy={(id, folder) => upload.pendingIn(id, folder).length > 0}
        />
      )}

      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <header className="h-12 shrink-0 flex items-center gap-2 px-3 md:px-4 border-b hairline">
          <KbBreadcrumb
            base={base}
            path={path}
            file={open?.kind === "file" ? open : undefined}
            onHome={() => onOpen(null)}
            onOpen={goTo}
            actions={{ onAdd: add, edits, onEditBase: () => setDialog({ kind: "edit-base" }), onDeleteBase: () => setDialog({ kind: "delete-base" }) }}
          />
        </header>

        {isPending || !base ? (
          <div className="px-4 md:px-5 pt-4 space-y-3">
            <Skeleton className="h-8 w-[320px] max-w-full rounded-[9px] bg-[var(--surface-2)]" />
            <Skeleton data-testid="node-skeleton" className="h-[180px] rounded-[18px] bg-[var(--surface-2)]" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-[14px] text-[var(--text-primary)] m-0">Could not load this base.</p>
            <p className="text-[12.5px] text-[var(--text-tertiary)] mt-1 mb-4">{error.message}</p>
            <GhostButton onClick={() => refetch()}>Retry</GhostButton>
          </div>
        ) : open?.kind === "file" ? (
          <KbFileView
            key={open.id}
            kbId={kbId}
            node={open}
            where={path.map((f) => f.name).join(" / ") || base.name}
            onBack={wide ? undefined : () => goTo(open.parent_id)}
            onRename={(name) => edits.rename(open, name)}
            onMove={() => edits.move(open)}
            onDelete={() => edits.remove(open)}
          />
        ) : (
          <KbFolderView
            kbId={kbId}
            base={base}
            folderId={folderId}
            tree={tree}
            listing={listing}
            pendingIn={(parentId) => upload.pendingIn(kbId, parentId)}
            me={settings?.user.id}
            onOpen={openNode}
            edits={edits}
            onDrop={(uploads, parentId) => upload.start(uploads, kbId, parentId)}
            onAdd={add}
            aside={!wide && <KbAddMenu place={here} onAdd={add} />}
          />
        )}
      </div>

      <NewFolderDialog
        open={dialog?.kind === "folder"}
        onOpenChange={(isOpen) => !isOpen && close()}
        onCreate={(name) => createFolder.mutate({ kbId, body: { name, parent_id: folderId ?? undefined } }, { onError: fail })}
        description={`Adds a folder to ${here}.`}
      />
      {dialog?.kind === "note" && <KbNoteDialog kbId={kbId} parentId={folderId} onClose={close} />}
      {dialog?.kind === "move" && <MoveNodeDialog node={dialog.node} tree={tree} onClose={close} />}
      {dialog?.kind === "delete" && (
        <DeleteNodeDialog node={dialog.node} fileCount={dialog.node.kind === "folder" ? tree.totals(dialog.node.id).files : 1} onClose={close} />
      )}
      {dialog?.kind === "edit-base" && base && <KbFormDialog base={base} onClose={close} />}
      {dialog?.kind === "delete-base" && base && <DeleteKbDialog base={base} onClose={close} />}
    </main>
  );
}
